/**
 * One place a screen turns an API call into something it can render.
 *
 * ── WHY THIS EXISTS, AND WHY IT IS NOT IN `api/client.ts` ────────────────────
 * `apps/web/src/api/client.ts` is the transport: it sends, it refuses to compose Bangla,
 * it refuses to invent a state, and it refuses to retry. What it deliberately does not
 * do is hold React state, and it must not — it is imported by tests that never mount a
 * component. This is the React half, and it is the only place in the app that turns a
 * thrown `ApiError` into the `ApiErrorBody` that `states/boundary.tsx` reads.
 *
 * It lives in `src/data/` rather than in one route group because four route groups need
 * it — activity, account, notifications and the preference screen — and a hook copied
 * four times is a hook that behaves four ways within a month.
 *
 * ── THE THREE FAILURES, AND WHY ONLY TWO OF THEM BECOME A SCREEN ─────────────
 *
 * `ApiError`         a designed failure. Its `code` is one of the nine, its `message_bn`
 *                    is the server's own Bangla, and `SurfaceStates` maps it to exactly
 *                    one drawn frame. Passed straight through.
 *
 * `NetworkError`     the request never left or the answer never arrived.
 *                    `api/client.ts` says in its own words that this "maps to
 *                    `E_OFFLINE`'s screen", so it is given that code — and an EMPTY
 *                    `message_bn`, because `OfflineState` renders the drawn frame and
 *                    reads no server sentence. Nothing here writes Bangla.
 *
 * `UnmappedApiError` a failure with no designed state. It is NOT turned into one. The
 *                    hook surfaces it separately and the screen renders nothing, which
 *                    is the honest outcome: `packages/contracts` has nine codes because
 *                    nine screens were drawn, and mapping a tenth condition onto one of
 *                    them is the change that would quietly undo docs/10 § WP-K.
 *                    It IS reported — `states/unmapped.ts` writes it to the console and
 *                    to a log an operator can read back. Invisible to the customer was
 *                    never meant to mean invisible to the business, and for a while it
 *                    did: a 500 from an unwired route rendered as an empty screen with
 *                    nothing anywhere to say a request had failed at all.
 *
 * A `<state>` resolved from `?state=` still wins over all of this, because the boundary
 * that wraps each route group runs before the screen mounts.
 *
 * ── A SIGNED-OUT CUSTOMER IS NOT A FAILURE, AND IS NOT TREATED AS ONE HERE ────
 * When the transport establishes that nobody is signed in (`api/client.ts § isSignedOut`)
 * the `ApiError` it throws carries `sub_code: 'no_identity'` in its body, and that body is
 * passed through like any other. It is `states/boundary.tsx` that turns it into the
 * sign-in door rather than a refusal — this hook's job is only to hand over the body the
 * server sent, so there is exactly one place that decides what a missing session looks
 * like.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiErrorBody } from '@oprappo/contracts';
import { ApiError, NetworkError, UnmappedApiError, isSignedOut } from '../api/client.js';
import { reportUnmapped } from '../states/unmapped.js';

/**
 * The body `states/boundary.tsx` reads. Built from a thrown error, never from nothing.
 *
 * Exported so a screen that loads outside this hook — a submit handler, a one-off read —
 * hands the boundary the same body this hook would, rather than building its own.
 */
export function toErrorBody(cause: unknown): ApiErrorBody | null {
  if (cause instanceof ApiError) return cause.body;
  if (cause instanceof NetworkError) {
    return {
      error: {
        code: 'E_OFFLINE',
        // EMPTY, and that is the rule rather than an oversight. `OfflineState` renders
        // the drawn frame's own copy; a sentence composed here would be Bangla this
        // codebase wrote (docs/01 § prohibited).
        message_bn: '',
        message_en: 'the request did not reach the API',
        // There is no server reference for a request that never reached a server. The
        // empty string says so; a generated uuid would look like something support could
        // look up.
        reference: '',
      },
    };
  }
  return null;
}

export interface ResourceOptions {
  /**
   * Load again every `pollMs` milliseconds while mounted — for a screen whose subject
   * moves on its own: an order being fulfilled, a payment an operator is confirming.
   *
   * A poll is QUIET, and each of the three ways it is quiet is a decision:
   *
   *   · it does not set `pending`, so a list does not flash to a skeleton every
   *     fifteen seconds;
   *   · a poll that FAILS leaves the last good `data` and `error` exactly as they were.
   *     A lost poll on a bad connection is not news — the next one will try again — and
   *     trading a screen full of real, slightly old data for ইন্টারনেট নেই would be the
   *     one thing `P0 States § ৭` says never to do: "Never blank a list that has usable
   *     cached data in it." The one exception is a session that has ENDED
   *     (`isSignedOut`): data that belongs to a person who is no longer signed in on
   *     this device is not left on the screen;
   *   · it waits while the page is hidden. A phone in a pocket spends no data on a screen
   *     nobody is looking at, and the first visible tick catches up.
   *
   * Absent, zero or negative: no polling, exactly as before this option existed.
   */
  pollMs?: number | undefined;
}

/**
 * Whether a poll tick should load now. Pure, so the rule is testable without a DOM:
 * never on top of a load that is still running, never while the page is hidden.
 */
export function pollDue(loading: boolean, visibility: string | undefined): boolean {
  return !loading && visibility !== 'hidden';
}

/**
 * Whether a failed load should replace what the screen shows. A first load or a reload
 * always does. A POLL does only when the session has ended — see `ResourceOptions`.
 */
export function failureReplacesData(quiet: boolean, cause: unknown): boolean {
  return !quiet || isSignedOut(cause);
}

export interface Resource<T> {
  data: T | null;
  /** A designed failure, ready for `<SurfaceStates error={…}>`. */
  error: ApiErrorBody | null;
  /** A failure with no designed state. Rendered as nothing, reported as itself. */
  unmapped: UnmappedApiError | null;
  pending: boolean;
  /** Re-runs the load. The drawn retry controls call this. */
  reload: () => void;
}

/**
 * Load one thing, once, and again when `key` changes.
 *
 * `key` rather than a dependency array: every caller here loads by a route parameter or
 * by a tab, and a string is something React can compare without an eslint rule telling
 * it how. A `load` function is re-created on every render and is deliberately NOT a
 * dependency — including it would refetch on every render, which on a chat screen is a
 * loop.
 */
export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  key: string,
  options: ResourceOptions = {},
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiErrorBody | null>(null);
  const [unmapped, setUnmapped] = useState<UnmappedApiError | null>(null);
  const [pending, setPending] = useState(true);
  const [nonce, setNonce] = useState(0);
  /**
   * Which load is a poll. A counter rather than a flag in state: the poll tick bumps it
   * together with `nonce`, and the effect compares the two, so a poll never needs a
   * render of its own to be recognised as one.
   */
  const polls = useRef(0);
  const lastPoll = useRef(0);
  /** A load is running. The poll tick skips rather than stacking a second one behind it. */
  const loading = useRef(false);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    const quiet = polls.current !== lastPoll.current;
    lastPoll.current = polls.current;
    loading.current = true;
    if (!quiet) setPending(true);

    load(controller.signal)
      .then((value) => {
        if (!live) return;
        setData(value);
        setError(null);
        setUnmapped(null);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        // The caller's own abort is not a failure and must not be dressed up as one.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        // Once, at the catch, never from a render. Ignores the nine designed failures;
        // see `states/unmapped.ts` for what is recorded and the three places it lands.
        reportUnmapped(key, cause);
        // A failed poll keeps what the screen already shows — see `ResourceOptions`.
        if (!failureReplacesData(quiet, cause)) return;
        setData(null);
        setError(toErrorBody(cause));
        setUnmapped(cause instanceof UnmappedApiError ? cause : null);
      })
      .finally(() => {
        if (!live) return;
        loading.current = false;
        if (!quiet) setPending(false);
      });

    return () => {
      live = false;
      loading.current = false;
      controller.abort();
    };
    // `load` is intentionally absent; see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  const pollMs = options.pollMs ?? 0;
  useEffect(() => {
    if (!(pollMs > 0)) return undefined;
    const timer = setInterval(() => {
      const visibility = typeof document === 'undefined' ? undefined : document.visibilityState;
      if (!pollDue(loading.current, visibility)) return;
      polls.current += 1;
      setNonce((n) => n + 1);
    }, pollMs);
    return () => {
      clearInterval(timer);
    };
  }, [pollMs, key]);

  return { data, error, unmapped, pending, reload };
}
